import { Injectable } from '@angular/core';
import {
    FormArray,
    FormControl,
    FormGroup,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { ICellRendererParams } from 'ag-grid-community';
import cloneDeep from 'lodash.clonedeep';
import { map, Observable, startWith } from 'rxjs';
import {
    nameOf,
    tryParseToNumber,
} from '../../../../../../core/functions/string-helpers';
import {
    FormGroupType,
    NestedFormGroupType,
} from '../../../../../../core/models/form-group-type';
import { ActionCode } from '../../../../../../core/models/resources/action-code';
import { ErpControlValidators } from '../../../../shared/constants/frequency-control-validators';
import { ApplicationFormControlManagerService } from '../../../../shared/services/application-form-control-manager/application-form-control-manager.service';
import { ApplicationStateService } from '../../../../shared/services/application-state/application-state.service';
import { SchedulesDAndHSharedFormService } from '../../../shared/services/schedules-d-and-h-shared-form/schedules-d-and-h-shared-form.service';
import { EmissionDesignator } from '../../models/emission-designator';
import { Frequency } from '../../models/frequency';
import { FrequencyFormModel } from '../../models/frequency-form-model';
import { EmissionDesignatorFormService } from '../emission-designator-form/emission-designator-form.service';
import { FrequencyFormattingService } from '../../../../../../core/services/frequency-formatting/frequency-formatting.service';

@Injectable({
    providedIn: 'root',
})
export class FrequencyFormService {
    private readonly locationNumberControlsToToggle: (keyof FrequencyFormModel)[] =
        ['antennaNumber'];

    private readonly antennaNumberControlsToToggle: (keyof FrequencyFormModel)[] =
        [
            'frequencyTypeCode',
            'frequencyLow',
            'frequencyHigh',
            'numberOfFrequenciesNeeded',
            'stationClassCode',
            'numberOfUnits',
            'powerOutput',
            'powerErp',
            'numberOfPagingReceivers',
        ];

    /* eslint-disable  @angular-eslint/prefer-inject */
    constructor(
        private readonly applicationStateService: ApplicationStateService,
        private readonly schedulesDAndHSharedFormService: SchedulesDAndHSharedFormService,
        private readonly emissionDesignatorFormService: EmissionDesignatorFormService,
        private readonly frequencyFormattingService: FrequencyFormattingService,
        private readonly applicationFormControlManager: ApplicationFormControlManagerService,
    ) {}
    /* eslint-enable  @angular-eslint/prefer-inject */

    getFormDefinition(): FormGroup<NestedFormGroupType<FrequencyFormModel>> {
        return new FormGroup<NestedFormGroupType<FrequencyFormModel>>({
            id: new FormControl<number | null>(null),
            locationNumber: new FormControl<number | null>(null),
            antennaNumber: new FormControl<number | null>(null),
            actionCode: new FormControl<ActionCode | null>(ActionCode.Add),
            isDeleted: new FormControl<boolean | null>(null),
            tempId: new FormControl<string | null>(null),
            emissionDesignatorsSelected: new FormControl<boolean>(false),
            emissionDesignators: new FormArray<
                FormGroup<FormGroupType<EmissionDesignator>>
            >([]),
            frequencyTypeCode: new FormControl<string | null>(null),
            frequencyLow: new FormControl<string | null>(null),
            frequencyHigh: new FormControl<string | null>(null),
            numberOfFrequenciesNeeded: new FormControl<number | null>(null),
            stationClassCode: new FormControl<string | null>(null),
            numberOfUnits: new FormControl<string | null>(null),
            powerOutput: new FormControl<string | null>(null),
            powerErp: new FormControl<string | null>(null),
            numberOfPagingReceivers: new FormControl<string | null>(null),
        });
    }

    /**
     * Toggles the validators for the frequency form.
     *
     * If the application is an existing license application and the frequency has not been added or
     * modified, then we should not apply the validators as the data from ULS should be considered valid (unless the user modifies the data).
     *
     * Otherwise, the validators should be applied to ensure that the data is valid if the user has added a new frequency or modified an existing frequency.
     *
     * @param frequency the frequency form group to toggle the validators on
     */
    toggleValidators(
        frequency: FormGroup<NestedFormGroupType<FrequencyFormModel>>,
    ): void {
        const isExistingLicenseApplication =
            this.applicationStateService.IsExistingLicenseApplication();
        const isAddedOrModified =
            frequency.dirty ||
            frequency.controls.actionCode.value === ActionCode.Add ||
            frequency.controls.actionCode.value === ActionCode.Modify;

        // If the application is an existing license application and the frequency has not been added or
        // modified, then we should not apply the validators as the data from the ULS should be
        // considered valid.
        if (isExistingLicenseApplication && !isAddedOrModified) {
            return;
        }

        const frequencyValidatorMap = this.getFormControlValidationMap();
        const frequencyValidatorMapKeys = Object.keys(
            frequencyValidatorMap,
        ) as Array<keyof FrequencyFormModel>;

        for (const controlName of frequencyValidatorMapKeys) {
            const control = frequency.controls[controlName];
            const validators = frequencyValidatorMap[controlName];

            if (control && validators) {
                control.setValidators(validators);
                control.updateValueAndValidity({ emitEvent: false });
            }
        }
    }

    /**
     * Gets the emission designators for the specified frequencies.
     * @param frequencies The form array of frequency form groups.
     * @param params The cell renderer parameters.
     * @returns An observable of emission designators.
     */
    getEmissionDesignators(
        frequencies: FormArray<
            FormGroup<NestedFormGroupType<FrequencyFormModel>>
        >,
        params: ICellRendererParams | undefined,
    ): Observable<EmissionDesignator[]> {
        return frequencies.valueChanges.pipe(
            startWith(cloneDeep((frequencies as FormArray).value)),
            map((frequencies) => {
                const index = params?.node.rowIndex;

                if (index === undefined || index === null) {
                    return [];
                }

                return frequencies[index]?.emissionDesignators;
            }),
        );
    }

    /**
     * Toggles the form controls based on the location number value.
     * @param frequency the frequency form group to update
     * @param locationNumber the location number for the frequency
     */
    toggleFormControlsForLocationChange(
        frequency: FormGroup<NestedFormGroupType<FrequencyFormModel>>,
        locationNumber: number | null,
    ): void {
        this.locationNumberControlsToToggle.forEach((controlName) => {
            const control = frequency.controls[controlName];

            if (locationNumber) {
                this.applicationFormControlManager.enable(
                    control,
                    { emitEvent: false },
                    () => {
                        control.markAsTouched();
                    },
                );
            } else {
                this.applicationFormControlManager.disable(
                    control,
                    { emitEvent: false },
                    () => {
                        control.reset();
                    },
                );
            }
        });
    }

    /**
     * Toggles the form controls based on the location number value.
     * @param frequency the frequency form group to update
     * @param locationNumber the location number for the frequency
     */
    toggleFormControlsForAntennaChange(
        frequency: FormGroup<NestedFormGroupType<FrequencyFormModel>>,
        antennaNumber: number | null,
    ): void {
        this.antennaNumberControlsToToggle.forEach((controlName) => {
            const control = frequency.controls[controlName];

            if (antennaNumber) {
                this.applicationFormControlManager.enable(
                    control,
                    { emitEvent: false },
                    () => {
                        control.markAsTouched();
                    },
                );
            } else {
                this.applicationFormControlManager.disable(
                    control,
                    { emitEvent: false },
                    () => {
                        control.reset();
                    },
                );
            }
        });
    }

    /**
     * Patches the form value with the response. This is necessary because the API response types are different from the form types.
     * The form values are strings, but the API response values are numbers because it gets serialized as numbers.
     */
    patchFormValue(
        form: FormArray<FormGroup<NestedFormGroupType<FrequencyFormModel>>>,
        response: Frequency[],
    ): void {
        form.patchValue(this.getFormModelValue(response));
    }

    /**
     * Gets the value of the form array. Some values need to be set to null if they are empty strings.
     * @param form
     * @returns
     */
    getValue(
        form: FormArray<FormGroup<NestedFormGroupType<FrequencyFormModel>>>,
    ): Frequency[] {
        return form.value.map((frequency) => {
            return {
                id: frequency.id,
                locationNumber: frequency.locationNumber,
                antennaNumber: frequency.antennaNumber,
                actionCode: frequency.actionCode,
                isDeleted: frequency.isDeleted,
                frequencyTypeCode: frequency.frequencyTypeCode,
                numberOfFrequenciesNeeded: frequency.numberOfFrequenciesNeeded,
                stationClassCode: frequency.stationClassCode,
                numberOfPagingReceivers: tryParseToNumber(
                    frequency.numberOfPagingReceivers,
                ),
                numberOfUnits: tryParseToNumber(frequency.numberOfUnits),
                emissionDesignators: frequency.emissionDesignators,
                frequencyLow: tryParseToNumber(frequency.frequencyLow),
                frequencyHigh: tryParseToNumber(frequency.frequencyHigh),
                powerErp: tryParseToNumber(frequency.powerErp),
                powerOutput: tryParseToNumber(frequency.powerOutput),
            };
        });
    }

    getFormModelValue(response: Frequency[]): FrequencyFormModel[] {
        return response.map((frequency) => {
            return {
                ...frequency,
                isDeleted:
                    frequency.actionCode === ActionCode.Delete ? true : null,
                tempId: null,
                numberOfPagingReceivers:
                    frequency.numberOfPagingReceivers?.toString() ?? null,
                numberOfUnits: frequency.numberOfUnits?.toString() ?? null,
                emissionDesignators:
                    this.emissionDesignatorFormService.getSortedEmissionDesignatorValues(
                        frequency.emissionDesignators,
                    ),
                frequencyLow: frequency.frequencyLow
                    ? this.frequencyFormattingService.getFormattedFrequency(
                          frequency.frequencyLow,
                      )
                    : null,
                frequencyHigh: frequency.frequencyHigh
                    ? this.frequencyFormattingService.getFormattedFrequency(
                          frequency.frequencyHigh,
                      )
                    : null,
                powerErp: frequency.powerErp?.toString() ?? null,
                powerOutput: frequency.powerOutput?.toString() ?? null,
                emissionDesignatorsSelected: false,
            };
        });
    }

    markActionCodeOnFrequencyChange(
        control: FormControl,
        frequencies: FormArray<
            FormGroup<NestedFormGroupType<FrequencyFormModel>>
        >,
        frequenciesResponse: Frequency[],
    ): boolean {
        return this.schedulesDAndHSharedFormService.markActionCodeOnChange(
            control,
            frequencies,
            this.getFormModelValue(frequenciesResponse),
            [
                ...this.schedulesDAndHSharedFormService.defaultOmitFields,
                nameOf<FrequencyFormModel>('emissionDesignatorsSelected'),
                nameOf<FrequencyFormModel>('frequencyTypeCode'),
            ],
        );
    }

    /**
     * Map of the form controls and their corresponding validators. Form controls, by default, will
     * not contain any validators. Validators get applied in specific scenarios and this map will
     * be used to apply the correct validators to the correct form controls in those scenarios.
     */
    private getFormControlValidationMap(): Partial<
        Record<keyof FrequencyFormModel, ValidatorFn[]>
    > {
        return {
            locationNumber: [Validators.required],
            antennaNumber: [Validators.required],
            emissionDesignators: [Validators.required],
            frequencyLow: [Validators.required],
            numberOfFrequenciesNeeded: [Validators.min(0)],
            stationClassCode: [Validators.required],
            numberOfUnits: [Validators.min(0)],
            powerOutput: [Validators.required, Validators.min(0)],
            powerErp: [...ErpControlValidators, Validators.required],
            numberOfPagingReceivers: [Validators.min(0)],
        };
    }
}
